package com.vizio.pay.api

import com.vizio.redwolf.auth.api.CreatePinInfo
import com.vizio.redwolf.auth.api.CreatePinResponse
import com.vizio.redwolf.auth.api.CreatePinResult
import com.vizio.redwolf.auth.api.DeletePinInfo
import com.vizio.redwolf.auth.api.DeletePinResponse
import com.vizio.redwolf.auth.api.DeletePinResult
import com.vizio.redwolf.auth.api.HasPinInfo
import com.vizio.redwolf.auth.api.HasPinResponse
import com.vizio.redwolf.auth.api.HasPinResult
import com.vizio.redwolf.auth.api.PinEnvironment
import com.vizio.redwolf.auth.api.PurchasePinApi
import com.vizio.redwolf.auth.api.UpdatePinInfo
import com.vizio.redwolf.auth.api.UpdatePinResponse
import com.vizio.redwolf.auth.api.UpdatePinResult
import com.vizio.redwolf.auth.api.ValidatePinInfo
import com.vizio.redwolf.auth.api.ValidatePinResponse
import com.vizio.redwolf.auth.api.ValidatePinResult
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import kotlin.js.Promise

// TODO
//  1. make more specific errors handling to avoid separate serialization for errors
//  2. Once name mangling issues are resolved, return type should be JsonObject

@JsExport
class PurchasePinJsApi(pinEnv: PinEnvironment, jsClientConfig: JsClientConfig? = null) {
    private val pinApi = PurchasePinApi(pinEnv = pinEnv,
                                        appConfig = AppConfig(""),
                                        clientConfig = jsClientConfig.toClientConfig(WebApiUseCase.SDK_HEADERS)
    )

    @JsName("createPin")
    fun createPin(jsCreatePinInfo: dynamic, sentryHeader: String? = null, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = PinRequests.CreatePin())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handlePinJsException) {
            val createPinInfo = CreatePinInfo(
                accessToken = jsCreatePinInfo.accessToken as String,
                pin = jsCreatePinInfo.pin as String
            )
            pinApi.createPin(createPinInfo, sentryHeader, traceId = traceId)
        }
    }

    @JsName("hasPin")
    fun hasPin(jsHasPinInfo: dynamic, sentryHeader: String? = null, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = PinRequests.HasPin())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handlePinJsException) {
            val hasPinInfo = HasPinInfo(
                accessToken = jsHasPinInfo.accessToken as String
            )
            pinApi.hasPin(hasPinInfo, sentryHeader, traceId = traceId)
        }
    }

    @JsName("validatePin")
    fun validatePin(jsValidatePinInfo: dynamic, sentryHeader: String? = null, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = PinRequests.ValidatePin())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handlePinJsException) {
            val validatePinInfo = ValidatePinInfo(
                accessToken = jsValidatePinInfo.accessToken as String,
                pin = jsValidatePinInfo.pin as String
            )
            pinApi.validatePin(validatePinInfo, sentryHeader, traceId = traceId)
        }
    }

    @JsName("deletePin")
    fun deletePin(jsDeletePinInfo: dynamic, sentryHeader: String? = null, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = PinRequests.DeletePin())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handlePinJsException) {
            val deletePinInfo = DeletePinInfo(
                accessToken = jsDeletePinInfo.accessToken as String
            )
            pinApi.deletePin(deletePinInfo, sentryHeader, traceId = traceId)
        }
    }

    @JsName("updatePin")
    fun updatePin(jsUpdatePinInfo: dynamic, sentryHeader: String? = null, traceId: String? = null): Promise<dynamic> {
        val data = JsExceptionInfo(caller = PinRequests.UpdatePin())
        return handleJsPromiseRequest(jsExceptionInfo = data, exceptionHandler = ::handlePinJsException) {
            val updatePinInfo = UpdatePinInfo(
                accessToken = jsUpdatePinInfo.accessToken as String,
                pin = jsUpdatePinInfo.pin as String
            )
            pinApi.updatePin(updatePinInfo, sentryHeader, traceId = traceId)
        }
    }

    private fun <T: Any> handlePinJsException(ex: Exception, data: JsExceptionInfo<T>? = null): dynamic {
        val exception = Exception(jsRequestExceptionError)
        return when (data?.caller) {
            is PinRequests.HasPin -> HasPinResult(ResponseHeaders.Invalid, HasPinResponse.Error(exception))
            is PinRequests.CreatePin -> CreatePinResult(ResponseHeaders.Invalid, CreatePinResponse.Error(exception))
            is PinRequests.ValidatePin -> ValidatePinResult(ResponseHeaders.Invalid, ValidatePinResponse.Error(exception))
            is PinRequests.UpdatePin -> UpdatePinResult(ResponseHeaders.Invalid, UpdatePinResponse.Error(exception))
            is PinRequests.DeletePin -> DeletePinResult(ResponseHeaders.Invalid, DeletePinResponse.Error(exception))
            else -> ex
        }
    }
}

private sealed class PinRequests {
    class HasPin : PinRequests()
    class CreatePin : PinRequests()
    class ValidatePin : PinRequests()
    class UpdatePin : PinRequests()
    class DeletePin : PinRequests()
}